/**
 * Sountsou 2026 - FAQ Accordion
 * Gestion interactive accessible (WAI-ARIA Accordion Pattern) et animations fluides
 */

export function initFaqAccordion(root = document) {
    const accordions = root.querySelectorAll('.sountsou-faq');

    accordions.forEach((accordionEl) => {
        // Évite la double initialisation sur le même élément
        if (accordionEl.dataset.faqInitialized === 'true') {
            return;
        }
        accordionEl.dataset.faqInitialized = 'true';

        const allowMultiple = accordionEl.dataset.allowMultiple !== 'false';
        const items = Array.from(accordionEl.querySelectorAll('[data-accordion-item]'));
        const triggers = items
            .map((item) => item.querySelector('[data-accordion-trigger]'))
            .filter(Boolean);

        const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

        // Fonction d'ouverture d'un volet
        function openItem(trigger, panel, item) {
            trigger.setAttribute('aria-expanded', 'true');
            item.classList.add('is-open');
            panel.removeAttribute('hidden');

            if (prefersReducedMotion) {
                panel.style.height = '';
                return;
            }

            // Réinitialise la hauteur à 0 pour démarrer l'animation
            panel.style.height = '0px';
            // Force le reflow du navigateur
            void panel.offsetHeight;

            const targetHeight = panel.scrollHeight;
            panel.style.height = `${targetHeight}px`;

            const onEnd = (e) => {
                if (e.target !== panel || e.propertyName !== 'height') return;
                panel.removeEventListener('transitionend', onEnd);
                if (trigger.getAttribute('aria-expanded') === 'true') {
                    panel.style.height = '';
                }
            };
            panel.addEventListener('transitionend', onEnd);
        }

        // Fonction de fermeture d'un volet
        function closeItem(trigger, panel, item) {
            trigger.setAttribute('aria-expanded', 'false');
            item.classList.remove('is-open');

            if (prefersReducedMotion) {
                panel.setAttribute('hidden', '');
                panel.style.height = '';
                return;
            }

            // Fixe la hauteur courante avant de la réduire à 0
            panel.style.height = `${panel.scrollHeight}px`;
            void panel.offsetHeight;
            panel.style.height = '0px';

            const onEnd = (e) => {
                if (e.target !== panel || e.propertyName !== 'height') return;
                panel.removeEventListener('transitionend', onEnd);
                if (trigger.getAttribute('aria-expanded') === 'false') {
                    panel.setAttribute('hidden', '');
                    panel.style.height = '';
                }
            };
            panel.addEventListener('transitionend', onEnd);
        }

        // Configuration pour chaque élément de la FAQ
        items.forEach((item, index) => {
            const trigger = item.querySelector('[data-accordion-trigger]');
            const panel = item.querySelector('[data-accordion-panel]');

            if (!trigger || !panel) {
                return;
            }

            // Clic sur le bouton déclencheur
            trigger.addEventListener('click', (e) => {
                e.preventDefault();
                const isCurrentlyOpen = trigger.getAttribute('aria-expanded') === 'true';

                if (isCurrentlyOpen) {
                    closeItem(trigger, panel, item);
                } else {
                    // Si le mode accordéon strict est activé, refermer les autres éléments
                    if (!allowMultiple) {
                        items.forEach((otherItem) => {
                            if (otherItem === item) return;
                            const otherTrigger = otherItem.querySelector('[data-accordion-trigger]');
                            const otherPanel = otherItem.querySelector('[data-accordion-panel]');
                            if (otherTrigger && otherPanel && otherTrigger.getAttribute('aria-expanded') === 'true') {
                                closeItem(otherTrigger, otherPanel, otherItem);
                            }
                        });
                    }
                    openItem(trigger, panel, item);
                }
            });

            // Navigation au clavier conforme aux recommandations WAI-ARIA APG
            trigger.addEventListener('keydown', (e) => {
                let targetIndex = -1;

                switch (e.key) {
                    case 'ArrowDown':
                        targetIndex = (index + 1) % triggers.length;
                        break;
                    case 'ArrowUp':
                        targetIndex = (index - 1 + triggers.length) % triggers.length;
                        break;
                    case 'Home':
                        targetIndex = 0;
                        break;
                    case 'End':
                        targetIndex = triggers.length - 1;
                        break;
                    default:
                        return;
                }

                if (targetIndex !== -1 && triggers[targetIndex]) {
                    e.preventDefault();
                    triggers[targetIndex].focus();
                }
            });
        });
    });
}

// Hook de rafraîchissement dynamique pour Gutenberg (ACF Block Preview)
if (typeof window !== 'undefined' && typeof window.acf !== 'undefined') {
    window.acf.addAction('render_block_preview/type=faq-accordion', function ($el) {
        if ($el && $el[0]) {
            initFaqAccordion($el[0]);
        }
    });
}
